/* Chirp Contour lander (cl-*). Each element registers once, so repeated script tags are safe. */
(() => {
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  const pad = (n) => String(n).padStart(2, '0');

  /* Countdown: data-mode="date" counts to data-end; data-mode="daily" counts to local midnight. */
  define('cl-countdown', class extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode;
      this.end = this.mode === 'date' ? Date.parse(this.dataset.end) : null;
      if (this.mode === 'date' && isNaN(this.end)) { this.hidden = true; return; }
      this.h = this.querySelector('[data-h]'); this.m = this.querySelector('[data-m]'); this.s = this.querySelector('[data-s]');
      this.tick(); this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    target() {
      if (this.mode === 'date') return this.end;
      const d = new Date(); d.setHours(24, 0, 0, 0); return d.getTime();
    }
    tick() {
      let ms = this.target() - Date.now();
      if (ms <= 0) { clearInterval(this.timer); if (this.dataset.hideExpired === 'true') this.hidden = true; ms = 0; }
      const t = Math.floor(ms / 1000);
      this.h.textContent = pad(Math.floor(t / 3600)); this.m.textContent = pad(Math.floor((t % 3600) / 60)); this.s.textContent = pad(t % 60);
    }
  });

  /* Carousel: scroll-snap track plus arrow buttons. */
  define('cl-carousel', class extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('[data-track]');
      const step = (dir) => {
        const card = track.firstElementChild; if (!card) return;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        track.scrollBy({ left: dir * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
      };
      this.querySelector('[data-prev]')?.addEventListener('click', () => step(-1));
      this.querySelector('[data-next]')?.addEventListener('click', () => step(1));
    }
  });

  /* Gallery: main image with thumbs and arrows. */
  define('cl-gallery', class extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-main]');
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.i = 0;
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.show(i)));
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.show(this.i - 1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.show(this.i + 1));
      /* Swipe left or right on the main image */
      const stage = this.querySelector('.cl-gallery__main'); let x0 = null, y0 = null;
      stage?.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
      stage?.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0, dy = e.changedTouches[0].clientY - y0; x0 = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.show(this.i + (dx < 0 ? 1 : -1));
      }, { passive: true });
      this.i = Math.max(0, this.thumbs.findIndex((t) => t.getAttribute('aria-current') === 'true'));
    }
    show(i) {
      if (!this.thumbs.length) return;
      this.i = (i + this.thumbs.length) % this.thumbs.length;
      const t = this.thumbs[this.i];
      this.main.src = t.dataset.src; this.main.srcset = t.dataset.srcset || ''; this.main.alt = t.dataset.alt || '';
      this.thumbs.forEach((x, n) => x.setAttribute('aria-current', n === this.i ? 'true' : 'false'));
      t.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    showMedia(id) {
      const n = this.thumbs.findIndex((t) => t.dataset.mediaId === String(id));
      if (n > -1) this.show(n);
      return n > -1;
    }
    /* Custom gallery images can be tagged with a color name. */
    showColor(color) {
      if (!color) return false;
      const n = this.thumbs.findIndex((t) => t.dataset.color && t.dataset.color === String(color).toLowerCase().trim());
      if (n > -1) this.show(n);
      return n > -1;
    }
  });

  /* Buy form: keeps variant id, price, and gallery in sync with the chosen swatch. */
  define('cl-buy-form', class extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('script[type="application/json"]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.idInput = this.querySelector('input[name="id"]');
      this.querySelectorAll('input[data-variant]').forEach((r) => r.addEventListener('change', () => this.update(r.value)));
      this.form = this.querySelector('form');
      this.root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const pf = this.querySelector('product-form');
      if (!this.form) return;
      if (pf && this.dataset.cartAction === 'drawer') {
        /* Extra gifts (after the first, which the theme adds via #promo_gift) join the same request. */
        const extras = (this.dataset.extraGifts || '').split(',').filter(Boolean);
        if (extras.length) pf.addEventListener('product-form:items', (e) => {
          const parent = e.detail.items[0];
          extras.forEach((id) => e.detail.items.push({ id: Number(id), quantity: 1, properties: { _Gift: id } }));
          if (parent && parent.properties && !parent.properties._ApplyGift) parent.properties._ApplyGift = extras[0];
        });
        /* Apply the discount code first, then hand off to the theme's add-to-cart and slide-out cart. */
        if (this.dataset.discount) {
          this.form.addEventListener('submit', async (e) => {
            if (this._handoff) return;
            e.preventDefault(); e.stopImmediatePropagation();
            if (this._busy) return; this._busy = true;
            const btn = this.form.querySelector('[type="submit"]'); if (btn) btn.disabled = true;
            await this.applyDiscount(this.dataset.discount);
            if (btn) btn.disabled = false;
            this._busy = false;
            if (typeof pf.handleSubmit === 'function') pf.handleSubmit();
            else { this._handoff = true; this.form.requestSubmit(); this._handoff = false; }
          }, true);
        }
        /* Keep the code on the cart if a later cart change drops it. */
        if (this.dataset.discount) document.addEventListener('cart:updated', (e) => this.ensureDiscount(e.detail && e.detail.cart));
        return;
      }
      /* Cart page or checkout: add main item and gifts in one request, then redirect. */
      const gifts = (this.dataset.giftIds || '').split(',').filter(Boolean);
      if (gifts.length || this.dataset.discount) this.form.addEventListener('submit', (e) => this.addAndRedirect(e, gifts));
    }
    /* Adds a code to the cart without removing codes that are already there. */
    async applyDiscount(code) {
      try {
        const cart = await fetch(this.root + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        const codes = (cart.discount_codes || []).map((d) => d.code);
        if (codes.some((c) => c.toLowerCase() === code.toLowerCase())) return true;
        const res = await fetch(this.root + 'cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: codes.concat(code).join(',') })
        });
        return res.ok;
      } catch (err) { console.warn('[cl-buy-form] discount apply failed', err); return false; }
    }
    async ensureDiscount(cart) {
      if (this._ensuring || !cart || !cart.items) return;
      const hasMain = cart.items.some((i) => this.variants.some((v) => Number(v.id) === Number(i.variant_id)));
      const code = this.dataset.discount.toLowerCase();
      const hasCode = (cart.discount_codes || []).some((d) => d.code.toLowerCase() === code);
      if (!hasMain || hasCode) return;
      this._ensuring = true;
      const ok = await this.applyDiscount(this.dataset.discount);
      if (ok && typeof CartManager !== 'undefined') CartManager.reloadMainCart();
      this._ensuring = false;
    }
    async addAndRedirect(e, gifts) {
      e.preventDefault();
      const btn = this.form.querySelector('[type="submit"]'); if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
      const main = { id: Number(this.idInput.value), quantity: 1, properties: {} };
      if (gifts[0]) main.properties._ApplyGift = gifts[0];
      const items = [main].concat(gifts.map((id) => ({ id: Number(id), quantity: 1, properties: { _Gift: id } })));
      try {
        if (this.dataset.discount) await this.applyDiscount(this.dataset.discount);
        const res = await fetch(this.root + 'cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items })
        });
        if (!res.ok) throw new Error(await res.text());
        window.location.href = this.root.replace(/\/$/, '') + (this.dataset.next || '/cart');
      } catch (err) {
        console.warn('[cl-buy-form] add failed, falling back to a plain form post', err);
        this.form.submit();
      }
    }
    update(id) {
      const v = this.variants.find((x) => String(x.id) === String(id)); if (!v) return;
      this.idInput.value = v.id;
      const name = this.querySelector('[data-variant-name]'); if (name) name.textContent = v.title;
      const now = this.querySelector('[data-price]'); if (now) now.textContent = v.price;
      const btn = this.querySelector('[type="submit"]');
      if (btn) { btn.disabled = !v.available; btn.querySelector('[data-label]').textContent = v.available ? btn.dataset.label : btn.dataset.soldout; }
      const gallery = document.querySelector(`#${this.dataset.gallery}`);
      if (gallery && !gallery.showColor?.(v.color) && v.media_id) gallery.showMedia(v.media_id);
      document.querySelectorAll('[data-sticky-price]').forEach((el) => { el.textContent = v.price; });
    }
  });

  /* UGC video card: tap to play with sound, tap again to pause. Only one plays at a time. */
  define('cl-ugc', class extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video'); this.btn = this.querySelector('[data-play]');
      if (!this.video) return;
      this.video.removeAttribute('controls'); this.video.playsInline = true; this.video.loop = true;
      this.btn?.addEventListener('click', () => this.toggle());
      this.video.addEventListener('click', () => this.toggle());
      this.video.addEventListener('pause', () => this.classList.remove('is-playing'));
      this.video.addEventListener('play', () => this.classList.add('is-playing'));
      if (this.dataset.preview === 'true' && 'IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.video.muted = true;
        new IntersectionObserver(([e]) => {
          if (this.dataset.user === 'true') return;
          if (e.isIntersecting) this.video.play().catch(() => {}); else this.video.pause();
        }, { threshold: 0.6 }).observe(this);
      }
    }
    toggle() {
      const v = this.video;
      if (this.dataset.user === 'true' && !v.paused) { v.pause(); this.btn?.setAttribute('aria-label', 'Play video'); return; }
      document.querySelectorAll('cl-ugc').forEach((o) => { if (o !== this && o.video && !o.video.paused) { o.dataset.user = 'false'; o.video.pause(); } });
      this.dataset.user = 'true'; v.muted = false; v.play().catch(() => { v.muted = true; v.play().catch(() => {}); });
      this.btn?.setAttribute('aria-label', 'Pause video');
    }
  });

  /* Tabs: filters review cards by data-tab. */
  define('cl-tabs', class extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.cards = [...this.querySelectorAll('[data-tab]')];
      this.tabs.forEach((t, i) => {
        t.addEventListener('click', () => this.select(i));
        t.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
            this.select(n); this.tabs[n].focus();
          }
        });
      });
      this.key = 'all'; this.page = 0;
      this.grid = this.querySelector('[data-track]');
      this.pager = this.querySelector('[data-pager]');
      this.dots = this.querySelector('[data-pager-dots]');
      this.status = this.querySelector('[data-pager-status]');
      this.querySelector('[data-page-prev]')?.addEventListener('click', () => this.go(this.page - 1));
      this.querySelector('[data-page-next]')?.addEventListener('click', () => this.go(this.page + 1));
      this.desktop = window.matchMedia('(min-width: 640px)');
      this.desktop.addEventListener?.('change', () => this.apply());
      let t; window.addEventListener('resize', () => { clearTimeout(t); t = setTimeout(() => this.apply(), 150); });
      if (this.tabs.length) this.select(0); else this.apply();
    }
    select(i) {
      const key = this.tabs[i]?.dataset.key || 'all';
      this.tabs.forEach((t, n) => { t.setAttribute('aria-selected', n === i); t.tabIndex = n === i ? 0 : -1; });
      this.key = key; this.page = 0; this.apply();
    }
    /* Cards per page = rows setting x the number of grid columns showing right now (3 on desktop, 2 on tablet). */
    pageSize() {
      const rows = Number(this.dataset.rows || 0);
      if (!rows || !this.desktop.matches || !this.grid) return 0;
      const cols = getComputedStyle(this.grid).gridTemplateColumns.split(' ').filter(Boolean).length || 3;
      return rows * cols;
    }
    go(n) {
      const pages = this.pages || 1;
      const next = Math.max(0, Math.min(pages - 1, n));
      if (next === this.page) return;
      this.page = next; this.apply();
      /* Keep the top of the grid in view after the page changes. */
      const top = this.grid.getBoundingClientRect().top;
      if (top < 80) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: top + window.scrollY - 120, behavior: reduce ? 'auto' : 'smooth' });
      }
    }
    /* Desktop and tablet: pages of cards with arrows and dots. Phones: one swipe row with every card. */
    apply() {
      const size = this.pageSize();
      const matches = this.cards.filter((c) => this.key === 'all' || c.dataset.tab === this.key);
      this.pages = size ? Math.max(1, Math.ceil(matches.length / size)) : 1;
      if (this.page > this.pages - 1) this.page = this.pages - 1;
      const from = this.page * size, to = from + size;
      this.cards.forEach((c) => {
        const i = matches.indexOf(c);
        c.hidden = i === -1 || (size > 0 && (i < from || i >= to));
        if (c.hidden) c.querySelector('video')?.pause();
      });
      if (this.pager) {
        const show = size > 0 && this.pages > 1;
        this.pager.hidden = !show;
        if (show) {
          this.querySelector('[data-page-prev]').disabled = this.page === 0;
          this.querySelector('[data-page-next]').disabled = this.page === this.pages - 1;
          if (this.status) this.status.textContent = `Page ${this.page + 1} of ${this.pages}`;
          if (this.dots) {
            this.dots.innerHTML = '';
            for (let n = 0; n < this.pages; n++) {
              const d = document.createElement('button');
              d.type = 'button'; d.className = 'cl-pager__dot';
              d.setAttribute('aria-label', `Go to page ${n + 1}`);
              if (n === this.page) d.setAttribute('aria-current', 'page');
              d.addEventListener('click', () => this.go(n));
              this.dots.appendChild(d);
            }
          }
        }
      }
      if (this.grid) this.grid.scrollLeft = 0;
    }
  });

  /* Sticky CTA: shows after the first screen, hides while the buy box is on screen. */
  define('cl-sticky', class extends HTMLElement {
    connectedCallback() {
      const bar = this.firstElementChild; const target = document.getElementById(this.dataset.target);
      let nearBuy = false;
      const update = () => bar.classList.toggle('is-on', window.scrollY > window.innerHeight * 0.9 && !nearBuy);
      if (target && 'IntersectionObserver' in window) {
        new IntersectionObserver(([e]) => { nearBuy = e.isIntersecting; update(); }).observe(target);
      }
      window.addEventListener('scroll', update, { passive: true }); update();
    }
  });

  /* Reveal on scroll, and fill stat bars. */
  const reveal = () => {
    const els = document.querySelectorAll('.cl-reveal:not(.is-in)');
    if (!('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { threshold: 0.15 });
    els.forEach((el) => io.observe(el));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reveal); else reveal();

  /* Smooth scroll for in-page links (e.g. every CTA that points at #cl-offer). */
  if (!window.__clSmooth) {
    window.__clSmooth = true;
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a || a.getAttribute('href').length < 2) return;
      const target = document.getElementById(decodeURIComponent(a.getAttribute('href').slice(1)));
      if (!target || !target.closest('.cl, .cl-section') && !target.classList.contains('cl')) return;
      e.preventDefault();
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const header = document.querySelector('header, .shopify-section-group-header-group');
      const offset = header && getComputedStyle(header).position.match(/sticky|fixed/) ? header.getBoundingClientRect().height : 0;
      const top = target.getBoundingClientRect().top + window.scrollY - offset - 8;
      window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
      history.replaceState(null, '', a.getAttribute('href'));
    });
  }
  document.addEventListener('shopify:section:load', reveal);
})();
